import * as React from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ChevronDown, Download, Sparkles } from 'lucide-react';
import type { BatchDetail, ProgressResponse, RunSummary } from '@tekvoice/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { StatusCountsRow } from '@/components/status-chip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/components/ui/sonner';
import { api } from '@/lib/api';
import { triggerBlobDownload } from '@/lib/audio';

const ABORT_WORDING: Record<string, string> = {
  elevenlabs_auth:
    'ElevenLabs rejected the API key, so the whole run was stopped. Fix the key in the environment, then start the run again.',
  elevenlabs_quota:
    'ElevenLabs ran out of credits, so the whole run was stopped before spending more. Top up the plan, then start the run again.',
  elevenlabs_forbidden:
    'ElevenLabs refused the request (403), so the whole run was stopped. Check the plan permissions for this voice or model.',
  cancelled_by_user: 'This run was cancelled. Rows that had already finished kept their audio.',
  unknown: 'This run was aborted before it finished. Rows that had already finished kept their audio.',
};

interface BatchHeaderProps {
  batch: BatchDetail;
  progress: ProgressResponse | undefined;
  onRequestGenerate: (scope: 'all' | 'missing' | 'failed' | 'stale', label: string) => void;
  onRunAborted: () => void;
  canGenerate: boolean;
  canExport: boolean;
  canAbort: boolean;
}

export function BatchHeader({
  batch,
  progress,
  onRequestGenerate,
  onRunAborted,
  canGenerate,
  canExport,
  canAbort,
}: BatchHeaderProps): React.JSX.Element {
  const [exportOpen, setExportOpen] = React.useState(false);
  const [bannerDismissed, setBannerDismissed] = React.useState(false);

  const activeRun: RunSummary | null = progress?.run ?? batch.activeRun;
  const lastRun = batch.lastRun;
  const confirmation = batch.exportConfirmation;

  const exportCsv = useMutation({
    mutationFn: () => api.batches.export(batch.id, confirmation.suggestedFilename),
    onSuccess: (file) => {
      triggerBlobDownload(file.blob, file.filename);
      toast.success(`Exported ${file.filename}`);
      setExportOpen(false);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const abort = useMutation({
    mutationFn: (runId: string) => api.runs.abort(runId),
    onSuccess: () => {
      toast.success('Run cancelled');
      onRunAborted();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const totals = activeRun?.totals;
  const percent =
    totals && totals.queued > 0
      ? Math.min(100, Math.round(((totals.done + totals.failed) / totals.queued) * 100))
      : 0;

  const showAbortBanner =
    !bannerDismissed && lastRun?.status === 'ABORTED' && activeRun?.status !== 'RUNNING';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/">
              <ArrowLeft className="h-4 w-4" />
              Batches
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{batch.name}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{batch.slug}</Badge>
            <span className="text-sm text-muted-foreground">
              {batch.rowCount} row(s) · {batch.approvedRowCount} fully approved
            </span>
            <StatusCountsRow counts={batch.statusCounts} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canGenerate ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={activeRun?.status === 'RUNNING'}>
                  <Sparkles className="h-4 w-4" />
                  Generate
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Generate audio for…</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onRequestGenerate('missing', 'missing audio')}>
                  Missing only
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onRequestGenerate('failed', 'failed clips')}>
                  Retry failed
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onRequestGenerate('stale', 'stale clips')}>
                  Stale (text changed)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onRequestGenerate('all', 'every row')}>
                  Everything (regenerate all)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}

          {canExport ? (
            <Button variant="outline" onClick={() => setExportOpen(true)}>
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          ) : null}
        </div>
      </div>

      {activeRun?.status === 'RUNNING' ? (
        <div className="rounded-lg border p-3" data-testid="run-progress">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium">
              Generating… {activeRun.totals.done + activeRun.totals.failed} of{' '}
              {activeRun.totals.queued} clip(s)
              {activeRun.totals.failed > 0 ? ` · ${activeRun.totals.failed} failed` : ''}
            </span>
            {canAbort ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={abort.isPending}
                onClick={() => abort.mutate(activeRun.id)}
              >
                Cancel run
              </Button>
            ) : null}
          </div>
          <Progress value={percent} aria-label="Generation progress" />
        </div>
      ) : null}

      {batch.deletedAt ? (
        <Alert data-testid="batch-deleted-banner">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>This batch was deleted</AlertTitle>
          <AlertDescription>
            It is read-only: rows cannot be edited, generated, approved or exported. The
            published audio in storage is untouched (SPEC §7 — delete is a soft delete).
          </AlertDescription>
        </Alert>
      ) : null}

      {/*
        C1 — an approval writes objects to GCS OUTSIDE the database transaction, so a crash
        between "objects replaced" and "result committed" leaves an intent marker behind.
        Boot repair re-drives or compensates them; anything still listed here needs a human.
      */}
      {batch.publishWarnings.length > 0 ? (
        <Alert variant="destructive" data-testid="publish-warning-banner">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {batch.publishWarnings.length} interrupted publish(es) need attention
          </AlertTitle>
          <AlertDescription>
            <ul className="list-inside list-disc">
              {batch.publishWarnings.slice(0, 5).map((warning) => (
                <li key={warning.rowId}>
                  Row {warning.rowIndex + 1} ({warning.publishedSlug}) — {warning.attemptCount}{' '}
                  attempt(s){warning.lastError ? `: ${warning.lastError}` : ''}. Approve the row
                  again to finish publishing it.
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {showAbortBanner && lastRun ? (
        <Alert
          variant={lastRun.abortReason === 'cancelled_by_user' ? 'default' : 'destructive'}
          data-testid="aborted-banner"
        >
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            Run stopped —{' '}
            {(lastRun.abortReason ?? 'unknown').replace(/_/g, ' ')}
          </AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>{ABORT_WORDING[lastRun.abortReason ?? 'unknown']}</span>
            <Button size="sm" variant="outline" onClick={() => setBannerDismissed(true)}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <ConfirmDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        title="Export CSV"
        description={
          <>
            Export — {confirmation.fullyApprovedRows} of {confirmation.totalRows} rows fully
            approved, {confirmation.staleRows} stale. Continue?
          </>
        }
        confirmLabel="Export"
        pending={exportCsv.isPending}
        onConfirm={() => exportCsv.mutate()}
      >
        <ul className="space-y-1 text-sm text-muted-foreground">
          <li>{confirmation.missingRows} row(s) still missing audio — their URL cells stay empty.</li>
          <li>{confirmation.failedRows} row(s) have a failed clip.</li>
          <li>{confirmation.blankArabicRows} row(s) have blank Arabic text.</li>
          {/* m7 — changing publishing.deliveryFormat does not re-publish approved rows. */}
          {confirmation.deliveryFormatMismatchRows > 0 ? (
            <li className="font-medium text-destructive" data-testid="delivery-format-mismatch">
              {confirmation.deliveryFormatMismatchRows} row(s) were published under a different
              delivery format — this file would mix formats. Re-approve them first.
            </li>
          ) : null}
          <li>
            File: <code className="rounded bg-muted px-1">{confirmation.suggestedFilename}</code>
          </li>
        </ul>
      </ConfirmDialog>
    </div>
  );
}
